/* import __COLOCATED_TEMPLATE__ from './trial-badge.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type FinTrialService from 'embercom/services/fin-trial-service';
import type IntlService from 'ember-intl/services/intl';
import type AppService from 'embercom/services/app-service';
import { AiAnswersState, selectedAgentToTrialType } from 'embercom/lib/operator/fin/types';
import moment from 'moment-timezone';

interface Arguments {
  canUseCustomAnswers?: boolean;
  selectedAgentValue?: string;
}

interface Signature {
  Args: Arguments;
  Element: HTMLDivElement;
}

export default class TrialBadge extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare finTrialService: FinTrialService;
  @service declare appService: AppService;

  private get usePerAgentTrials(): boolean {
    return (
      this.appService.app.canUseFeature('fin-agentic-trials') && !!this.args.selectedAgentValue
    );
  }

  private get agentTrialType() {
    return selectedAgentToTrialType(this.args.selectedAgentValue ?? 'fin');
  }

  get showStamp() {
    if (this.usePerAgentTrials) {
      return (
        this.finTrialService.showAgentCTAs(this.agentTrialType) && !this.args.canUseCustomAnswers
      );
    }
    return this.finTrialService.showCTAs && !this.args.canUseCustomAnswers;
  }

  get stampText() {
    if (!this.showStamp) {
      return '';
    }

    if (this.usePerAgentTrials) {
      let agentState = this.finTrialService.getAgentTrialState(this.agentTrialType);
      if (agentState?.state === AiAnswersState.trialStarted && agentState.trialDetails?.endDate) {
        let remainingDays = moment(agentState.trialDetails.endDate).diff(moment(), 'days');
        return this.intl.t('operator.fin.free-trial-copy.trial-badge.trial-ends-in-days', {
          remainingDays,
        });
      }
      return this.intl.t('operator.fin.free-trial-copy.trial-badge.free-for-14-days');
    }

    if (this.finTrialService.freeTrialStarted) {
      return this.intl.t('operator.fin.free-trial-copy.trial-badge.days-free-remaining', {
        remainingDays: this.finTrialService.trialDaysRemaining,
      });
    }
    return this.intl.t('operator.fin.free-trial-copy.trial-badge.free-for-14-days');
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::Fin::TrialBadge': typeof TrialBadge;
  }
}
